import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { ReduxState } from '../../AppLoader/types';
import { ContentEditSegment, PlanClassification } from '../../auth/AuthTypes';
import { StockItem } from '../../common/types/StockItemTypes';
import ContentEditAPI from './ContentEditAPI';
import ContentEditStateDefinition from './ContentEditStateDefinition';

export enum TaskStatus {
  SUCCEEDED = 'SUCCEEDED',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  THROTTLED = 'THROTTLED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export const purchaseInfo = createAsyncThunk(
  'contentEdit/purchaseInfo',
  async () => {
    const response = await ContentEditAPI.getPurchaseInfo();
    const json = await response.json();
    return json;
  }
);

const contentEditSlice = createSlice({
  name: 'contentEdit',
  initialState: ContentEditStateDefinition.getInitialState(),
  reducers: {
    startPolling: (
      state,
      action: PayloadAction<{
        stockItem: StockItem;
        promptText?: string;
        selection?: { startTime: number; duration: number };
      }>
    ) => {
      state.isPolling = true;
      state.error = undefined;
      state.modalState = 'loading';
      state.stockItemEdited = action.payload.stockItem;
      state.lastPromptText = action.payload.promptText;
      state.lastSelection = action.payload.selection;
    },
    updateStatus: (state, action: PayloadAction<TaskStatus>) => {
      state.status = action.payload;
    },
    videoSucceeded: (
      state,
      action: PayloadAction<{
        outputUrl: string;
        stockItemEdited: StockItem;
        jobId: number;
      }>
    ) => {
      state.isPolling = false;
      state.modalState = 'result';
      state.outputUrl = action.payload.outputUrl;
      state.status = TaskStatus.SUCCEEDED;
      state.stockItemEdited = action.payload.stockItemEdited;
      state.jobId = action.payload.jobId;
      state.resultSeen = false;
    },
    videoFailed: (
      state,
      action: PayloadAction<{ error: string; jobId?: number }>
    ) => {
      state.isPolling = false;
      state.modalState = 'edit';
      state.error = action.payload.error;
      state.jobId = action.payload.jobId;
    },
    stopPolling: (state) => {
      state.isPolling = false;
    },
    resetModalState: (state) => {
      state.modalState = 'edit';
      state.status = null;
      state.outputUrl = undefined;
      state.error = undefined;
      state.stockItemEdited = undefined;
      state.selectedStockItem = undefined;
      state.resultSeen = false;
    },
    resetToEdit: (state) => {
      state.modalState = 'edit';
      state.status = null;
      state.outputUrl = undefined;
      state.error = undefined;
      state.resultSeen = false;
    },
    markResultSeen: (state) => {
      state.resultSeen = true;
    },
    openVideoEditorModal: (state, action: PayloadAction<StockItem>) => {
      state.isOpen = true;

      if (state.modalState === 'result' && !state.resultSeen) {
        // Results haven't been viewed yet — force the result view with the
        // original stock item that was actually used for generation.
        state.selectedStockItem = state.stockItemEdited;
      } else {
        state.stockItemEdited = action.payload;
        state.selectedStockItem = action.payload;
      }
    },
    closeVideoEditorModal: (state) => {
      state.isOpen = false;
      state.selectedStockItem = undefined;
    },
    setCreditBalance: (state, action: PayloadAction<number>) => {
      state.creditBalance = action.payload;
    },
    updateOptIns: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.optIns = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(purchaseInfo.pending, (state) => {
        state.isFetchingPurchaseInfo = true;
      })
      .addCase(purchaseInfo.fulfilled, (state, action) => {
        state.isFetchingPurchaseInfo = false;
        if (action.payload.data) {
          state.purchaseInfo = action.payload.data;
        }
      })
      .addCase(purchaseInfo.rejected, (state, action) => {
        state.isFetchingPurchaseInfo = false;
        state.purchaseInfoError =
          action.error.message || 'Failed to fetch purchase info';
      });
  },
});

export const {
  startPolling,
  updateStatus,
  videoSucceeded,
  videoFailed,
  stopPolling,
  setCreditBalance,
  resetModalState,
  resetToEdit,
  markResultSeen,
  openVideoEditorModal,
  closeVideoEditorModal,
  updateOptIns,
} = contentEditSlice.actions;

export default contentEditSlice.reducer;

// Selectors
export const selectContentEditState = (state: ReduxState) => state.contentEdit;
export const selectIsPolling = (state: ReduxState) =>
  state.contentEdit.isPolling;
export const selectModalState = (state: ReduxState) =>
  state.contentEdit.modalState;
export const selectVideoEditorModalIsOpen = (state: ReduxState) =>
  state.contentEdit.isOpen;
export const selectVideoEditorModalStockItem = (state: ReduxState) =>
  state.contentEdit.selectedStockItem;
export const selectStockItemEdited = (state: ReduxState) =>
  state.contentEdit.stockItemEdited;
export const selectVideoStatus = (state: ReduxState) =>
  state.contentEdit.status;
export const selectOutputUrl = (state: ReduxState) =>
  state.contentEdit.outputUrl;
export const selectVideoError = (state: ReduxState) => state.contentEdit.error;
export const selectCreditBalance = (state: ReduxState) =>
  state.contentEdit.creditBalance || 0;
export const selectVideoState = (state) => state.contentEdit;
export const selectJobId = (state: ReduxState) => state.contentEdit.jobId;
export const selectLastPromptText = (state: ReduxState) =>
  state.contentEdit.lastPromptText;
export const selectLastSelection = (state: ReduxState) =>
  state.contentEdit.lastSelection;
export const selectShouldShowAIEditFeatures = (state: ReduxState) => {
  return state.auth.featureFlags.aiVideoEditUSEcommSMB;
};

export const selectAIEditButtonVisible = (state: ReduxState): boolean => {
  return state.auth?.permissions?.aiFeaturesVisible ?? false;
};

export const selectHasContentEditPermission = (state: ReduxState): boolean => {
  return state.auth?.permissions?.hasContentEditPermission ?? false;
};

export const selectResultSeen = (state: ReduxState) =>
  state.contentEdit.resultSeen;
export const selectOptIns = (state: ReduxState) => state.contentEdit.optIns;

/**
 * Client-side calculation of content edit segment
 * Determines what CTA to show in the AI Edit modal
 * Uses subscription.organizationId to determine enterprise status
 *
 */
export const selectContentEditSegment = (
  state: ReduxState
): ContentEditSegment => {
  const isLoggedIn = state.auth?.isLoggedIn ?? false;
  const isAIEditButtonVisible = selectAIEditButtonVisible(state);
  const hasContentEditPermission = selectHasContentEditPermission(state);

  if (!isAIEditButtonVisible) {
    return 'disabled';
  }

  // Check if user has any subscription
  const hasSubscription =
    state.auth?.subscription !== null && state.auth?.subscription !== undefined;

  // Not logged in OR logged in without subscription → both redirect to pricing
  if (!isLoggedIn || !hasSubscription) {
    return 'unsubscribed';
  }

  const subscriptionStatus = state.auth?.subscription?.status;
  const hasActiveOrCancelledSubscription =
    subscriptionStatus === 'active' ||
    // accounts for both legacy and recurly subscription statuses
    subscriptionStatus === 'canceled' ||
    subscriptionStatus === 'donotbill';
  if (!hasActiveOrCancelledSubscription) {
    return 'unsubscribed';
  }

  // For users with subscriptions, check ContentEdit permission
  if (hasContentEditPermission) {
    return 'subscribed_user';
  }

  const isEnterprise =
    state.auth?.subscription?.planClassification ===
    PlanClassification.ENTERPRISE_PLAN_CLASSIFICATION;

  if (isEnterprise) {
    return 'enterprise_no_permission';
  }

  return 'upgradable_user';
};
